<script setup lang="ts">
import { computed } from "vue";

import { linkActions } from "@/action/linkActions";
import MenuItemDelimiter from "@/components/menu/MenuItemDelimiter.vue";
import ObjectiveCardAttributes from "@/components/objectives/ObjectiveCardAttributes.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import { useLinkDropTarget } from "@/composables/linkDrag";
import type { BoardData } from "@/model/board";
import type { Objective } from "@/model/objective";
import { useArtObjectivesPanelStore } from "@/store/artObjectivesPanel";

const props = defineProps<{
  objective: Objective;
  board: BoardData<"team">;
  index: number;
  reserveActualValue: boolean;
}>();

const panelStore = useArtObjectivesPanelStore();

const { isDropTarget, onPointerEnter, onPointerLeave } = useLinkDropTarget(
  () => ({ kind: "objective", boardId: props.board.id, id: props.objective.id }),
  {
    isValid: (source) => {
      if (source.kind === "objective") {
        return !props.objective.linkedObjectives.some((l) => l.id === source.id);
      }
      return true;
    },
  },
);

// Un-dim while this cell is a valid drop target to preview the post-link state.
const isDimmed = computed(
  () => !panelStore.isTeamObjectiveLinkedToPinned(props.objective) && !isDropTarget.value,
);

const linkToPinned = computed(() => {
  const pinnedId = panelStore.pinnedArtObjectiveId;
  if (!pinnedId) return null;
  return props.objective.linkedObjectives.find((l) => l.id === pinnedId) ?? null;
});

function removeLinkToPinned() {
  const link = linkToPinned.value;
  if (!link) return;
  void linkActions.removeObjectiveToObjective(
    "art-objectives-board",
    link.boardId,
    link.id,
    props.board.id,
    props.objective.id,
  );
}

/** Suppress the tooltip when the cell is dimmed*/
function shouldShowTooltip(): false | void {
  if (isDimmed.value) return false;
}
</script>

<template>
  <article
    class="art-objective-cell"
    :class="{ dimmed: isDimmed, 'drop-target': isDropTarget }"
    :data-id="objective.id"
    @pointerenter="onPointerEnter"
    @pointerleave="onPointerLeave"
  >
    <span class="index">{{ index }}</span>
    <BaseTooltip
      position="top"
      :max-width="500"
      :on-show="shouldShowTooltip"
      :delay="[500, 50]"
      class="title-wrapper"
    >
      <div class="title">{{ objective.text }}</div>

      <template #content>
        <div class="objective-cell-tooltip-content">
          <h3 class="tooltip-title">{{ objective.text }}</h3>
          <p v-if="objective.description" class="tooltip-description">
            {{ objective.description }}
          </p>
        </div>
      </template>
    </BaseTooltip>
    <div class="meta-row">
      <div class="value-chip">
        <div class="value">
          <span class="value-text">{{ objective.bv }}</span>
        </div>
        <BaseTooltip class="value-tooltip">
          <span class="value-label">{{ $t("objectives.bv") }}</span>
          <template #content>{{ $t("objectives.businessValue") }}</template>
        </BaseTooltip>
      </div>
      <div
        v-if="reserveActualValue"
        class="value-chip"
        :class="{ 'without-value': objective.av === null }"
      >
        <div class="value">
          <span class="value-text">{{ objective.av }}</span>
        </div>
        <BaseTooltip class="value-tooltip">
          <span class="value-label">{{ $t("objectives.av") }}</span>
          <template #content>{{ $t("objectives.actualValue") }}</template>
        </BaseTooltip>
      </div>
      <MenuItemDelimiter compact />
      <ObjectiveCardAttributes :objective :board has-status-distribution board-scale compact />
      <div class="remove-link-slot">
        <BaseTooltip v-if="linkToPinned" position="top">
          <button
            type="button"
            class="remove-link"
            data-no-screenshot
            :aria-label="$t('objectives.removeLink')"
            @click.stop="removeLinkToPinned"
          >
            <SvgIcon name="system/minus-circled" width="100%" height="100%" />
          </button>
          <template #content>{{ $t("objectives.removeLink") }}</template>
        </BaseTooltip>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
@use "@/styles/board";
@use "@/styles/font";
@use "@/styles/mixins/typography";
@use "@/styles/variables/colors";

// 2×2 grid: col-1 index, col-2 spans title (row-1) and meta-row (row-2).
// Row 1 reserves space for two-line titles (14 × 1.4 × 2).
.art-objective-cell {
  display: grid;

  // minmax floor on the track, not on .index — em-units on .index would
  // compound through its own board.len(14px) font-size.
  grid-template-columns: minmax(board.len(16px), auto) 1fr;
  grid-template-rows: board.len(40px) auto;
  align-content: center;
  gap: board.len(12px) board.len(8px);
  height: 100%;
  width: 100%;
  padding: board.len(12px);
  background: none;
  border-radius: board.len(6px);

  &.dimmed {
    opacity: 0.3;
  }

  // Inset outline so layout doesn't shift.
  &.drop-target {
    outline: board.len(2px) solid colors.$border-subtle;
    outline-offset: board.len(-2px);
  }
}

// Always reserved, so the status bar keeps its width when a pin makes the
// button appear. `margin-left: auto` holds it at the row's right edge in
// planning mode, where the bar isn't there to push it.
.remove-link-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 board.len(14px);
  height: board.len(14px);
  margin-left: auto;
}

.remove-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: board.len(14px);
  height: board.len(14px);
  padding: 0;
  border: none;
  background: transparent;
  color: colors.$destructive;
  cursor: pointer;
}

.index {
  grid-column: 1;
  grid-row: 1;
  align-self: start;
  font-size: board.len(14px);
  line-height: 1.4;
  font-weight: font.$weight-semi-bold;
  color: colors.$text-subtle;
}

// BaseTooltip renders a `display: inline-block` <span> — force it to block
// so the inner .title's -webkit-line-clamp can break to 2 lines.
.art-objective-cell > .title-wrapper {
  grid-column: 2;
  grid-row: 1;
  display: block;
  min-width: 0;
  width: 100%;
}

.title {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-size: board.len(14px);
  line-height: 1.4;
  font-weight: font.$weight-semi-bold;
  color: colors.$text;
}

// No font-size override here — board.len-based child sizes would compound
// otherwise. Children set their own font-size where needed.
.meta-row {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  align-items: center;
  gap: board.len(8px);
  height: board.len(20px);
  min-width: 0;
}

.value-chip.without-value {
  visibility: hidden;
}

.value-chip {
  display: flex;
  align-items: center;
  gap: board.len(4px);
  flex-shrink: 0;
  color: colors.$text-subtle;
}

// Font-size lives on the BaseTooltip span so its line-box height matches
// the value box. Without it, the span inherits the larger cascaded font and the
// label drops to the bottom of an oversized line-box.
.value-tooltip {
  font-size: board.len(13px);
  line-height: 1;
}

.value-label {
  font-weight: font.$weight-medium;
}

// Dimensions on .value (no font-size override, so em resolves at inherited
// scale); font-size on inner .value-text. Otherwise h/min-w/padding would
// compound through the digit font-size and shrink dramatically.
.value {
  display: flex;
  box-sizing: border-box;
  height: board.len(20px);
  min-width: board.len(24px);
  padding: board.len(4px) board.len(8px);
  align-items: center;
  justify-content: center;
  background-color: colors.$background-grey;
  border-radius: board.len(4px);
}

.value-text {
  font-size: board.len(10px);
  font-weight: font.$weight-semi-bold;
  color: colors.$text-subtle;
  line-height: 1;
}

// Tooltip content is teleported to <body>, off the scaling board, so
// use real px via typography mixins, not board.len.
.objective-cell-tooltip-content {
  .tooltip-title {
    @include typography.medium("semi-bold");
  }

  // Preserve author line breaks
  .tooltip-description {
    @include typography.small("regular");

    white-space: pre-wrap;
    color: colors.$text-subtle-inverse;
    margin-top: 6px;
  }
}
</style>
